import { v1Api } from "../apis";
import ACTION from "./actionNames";
import {
  EventFilters,
  EventMeeting,
  EventDetails,
  EventMedia,
  EventCategory,
  Bookmark,
  ParticipationMethodEnum,
  PositionMethodEnum,
} from "../types/types.event";
import { AgendaEvent } from "../types/types.agenda";
import { SearchResult } from "../types/types.search";
import { PortalCustomization } from "../types/types.customization";
import {
  ApiResponseValueKey,
  JwplayTracks,
  JwplayerVideo,
} from "../types/types";
import { AxiosError, AxiosResponse } from "axios";
import { AppDispatch } from "../app/store";
import { toLocalISODateString } from "../utils";

export interface Action<T> {
  type: number;
  payload?: T;
}

type ApiError = {
  response: {
    data: {
      message?: string;
      Message?: string;
    };
  };
};

type Params = {
  $filter?: string;
  $orderby?: string;
  $skip?: string;
  $top?: string;
};
type GetEventConfig = {
  params: Params;
};

export const getEventsListAsync = (
  filters: EventFilters | null = null,
  loadPastEvents: boolean,
  loadNextEvents: boolean,
  initialLoad: boolean
) => {
  return async (
    dispatch: AppDispatch
  ): Promise<Action<EventMeeting[] | undefined>> => {
    const getAndOperator = () => {
      return config.params.$filter ? " and" : "";
    };
    const config: GetEventConfig = {
      params: {
        $filter: "",
        $orderby: "startDateTime",
      },
    };

    const today = filters?.calendarDate
      ? new Date(filters.calendarDate)
      : new Date();
    today.setHours(0, 0, 0);
    if (!filters?.calendarDate) {
      today.setDate(today.getDate());
    }

    if (filters) {
      if (filters.categories?.length) {
        config.params.$filter += `categoryId in (${filters.categories.join()})`;
      }
      if (!loadPastEvents) {
        dispatch(cleanSearchResults());
        if (filters.date?.from) {
          const fromDate = new Date(filters.date.from);
          config.params.$filter += `${getAndOperator()} startDateTime ge ${toLocalISODateString(
            fromDate
          )}`;
        }
        if (filters.date?.to) {
          const toDate = new Date(filters.date.to);
          toDate.setDate(toDate.getDate() + 1);

          config.params.$filter += `${getAndOperator()} startDateTime lt ${toLocalISODateString(
            toDate
          )}`;
        }
        // If there is only an end date filter, we reverse the list orders.
        if (!filters.date?.from && filters.date?.to) {
          config.params.$orderby = `startDateTime desc, eventName desc`;
        } else {
          config.params.$orderby = `startDateTime asc, eventName asc`;
        }
        // If there are no date filters, we use today as the starting point
        if (!filters.date?.from && !filters.date?.to) {
          config.params.$filter += `${getAndOperator()} startDateTime ge ${toLocalISODateString(
            today
          )}`;
        }
        if (loadNextEvents && filters.skip) {
          config.params.$skip = `${filters.skip}`;
        }
      } else {
        if (filters.loadPastDate?.from) {
          config.params.$filter += `${getAndOperator()} startDateTime ge ${toLocalISODateString(
            new Date(filters.loadPastDate.from)
          )}`;
        }
        if (filters.loadPastDate?.to) {
          const pastToDate = new Date(filters.loadPastDate.to);
          pastToDate.setDate(pastToDate.getDate() + 1);
          config.params.$filter += `${getAndOperator()} startDateTime lt ${toLocalISODateString(
            pastToDate
          )}`;
        }
        if (filters.loadPastDate?.skip) {
          config.params.$skip = `${filters.loadPastDate.skip}`;
        }
        config.params.$orderby = `startDateTime desc, eventName desc`;
      }
    }
    if (!config.params.$filter) {
      // If there is no filters, return the last events from Yesterday
      config.params.$filter += `${getAndOperator()} startDateTime ge ${toLocalISODateString(
        today
      )}`;
    }

    const requestUrl = `/Events`;

    if (
      initialLoad &&
      !filters?.calendarDate &&
      !filters?.date?.from &&
      !filters?.date?.to
    ) {
      const initialLoadPastEventPageSize = 5;
      let initiallyLoadedEvents: EventMeeting[] = [];
      const categoryFilter =
        filters && filters.categories?.length
          ? ` and categoryId in (${filters.categories.join()})`
          : "";
      config.params = {};
      config.params.$filter = `startDateTime lt ${toLocalISODateString(
        today
      )}${categoryFilter}`;
      config.params.$orderby = "startDateTime desc, eventName desc";

      await v1Api
        .get(requestUrl, config)
        .then(
          ({ data }: AxiosResponse<ApiResponseValueKey<EventMeeting[]>>) => {
            if (data?.value) {
              initiallyLoadedEvents = data.value.slice(
                0,
                initialLoadPastEventPageSize
              );
            }
            const hasPastLink =
              Boolean(data["@odata.nextLink" as keyof typeof data]) &&
              data.value.length > 0;
            dispatch(updatePastLink(hasPastLink));
            return;
          }
        );

      config.params = {};
      config.params.$filter = `startDateTime ge ${toLocalISODateString(
        today
      )}${categoryFilter}`;
      config.params.$orderby = "startDateTime asc, eventName asc";

      await v1Api
        .get(requestUrl, config)
        .then(
          ({ data }: AxiosResponse<ApiResponseValueKey<EventMeeting[]>>) => {
            if (data?.value) {
              initiallyLoadedEvents = initiallyLoadedEvents.concat(data.value);
            }
            const hasNextLink =
              Boolean(data["@odata.nextLink" as keyof typeof data]) &&
              data.value.length > 0;
            dispatch(updateNextLink(hasNextLink));
            return;
          }
        );

      return dispatch({
        type: ACTION.GET_EVENTS_LIST,
        payload:
          initiallyLoadedEvents.length > 0 ? initiallyLoadedEvents : undefined,
      });
    }

    return v1Api
      .get(requestUrl, config)
      .then(({ data }: AxiosResponse<ApiResponseValueKey<EventMeeting[]>>) => {
        const hasNextLink =
          Boolean(data["@odata.nextLink" as keyof typeof data]) &&
          data.value.length > 0;

        if (!loadPastEvents && !loadNextEvents) {
          const fromDate = filters?.date?.from;
          const calendarDate = filters?.calendarDate;
          const toDate = filters?.date?.to;

          const notAllowPastLink = fromDate || calendarDate;
          dispatch(updatePastLink(notAllowPastLink ? false : hasNextLink));

          const notAllowNextLink = !fromDate && !calendarDate && toDate;
          dispatch(updateNextLink(notAllowNextLink ? false : hasNextLink));
        } else if (loadNextEvents) {
          dispatch(updateNextLink(hasNextLink));
        } else {
          if (loadPastEvents) {
            dispatch(updatePastLink(hasNextLink));
          }
        }

        const isLoadingMore = loadPastEvents || loadNextEvents;
        return dispatch({
          type: isLoadingMore
            ? ACTION.GET_NEXT_PAST_EVENTS_LIST
            : ACTION.GET_EVENTS_LIST,
          payload: data?.value || null,
        });
      });
  };
};

export const cleanEventList = (): Action<undefined> => {
  return {
    type: ACTION.CLEAN_EVENT_LIST,
  };
};

export const getEventDetails = (
  eventDetails: EventDetails
): Action<EventDetails> => {
  return {
    type: ACTION.GET_EVENT_DETAILS,
    payload: eventDetails,
  };
};

export const getEventDetailsAsync = (eventId: number) => {
  return (dispatch: AppDispatch): Promise<Action<EventDetails>> => {
    return v1Api
      .get(`/Events/${eventId}`)
      .then(({ data }: AxiosResponse<EventDetails>) =>
        dispatch(getEventDetails(data))
      );
  };
};
export const cleanEventDetails = (): Action<undefined> => {
  return {
    type: ACTION.CLEAN_EVENT_DETAILS,
  };
};

export const getAgendaEvent = (agenda: AgendaEvent): Action<AgendaEvent> => {
  return {
    type: ACTION.GET_AGENDA_EVENT,
    payload: agenda,
  };
};
export const cleanAgendaEvent = (): Action<undefined> => {
  return {
    type: ACTION.CLEAN_AGENDA_EVENT,
  };
};

export const getAgendaEventAsync = (
  id: number
):
  | Action<AgendaEvent>
  | ((dispatch: AppDispatch) => Promise<Action<AgendaEvent>>) => {
  if (!id) {
    return getAgendaEvent({
      id: -1,
      items: [],
      publishedFiles: [],
      agendaPacketIsPublish: false,
      agendaIsPublish: false,
      enablePublicSpeakerSignup: false,
      enableWrittenComment: false,
      publicCommentRequireUserLogin: false,
      speakerSignupRequireUserLogin: false,
      publishUnlockedAgendaItems: false,
      commentCutOffTime: 0,
      currentDate: new Date(1900, 1, 1),
      signUpCutoffTime: 0,
      allowAttachmentsOnWrittenComments: false,
    });
  }
  return (dispatch: AppDispatch): Promise<Action<AgendaEvent>> => {
    return v1Api
      .get(`/Meetings/${id}`)
      .then(({ data }: AxiosResponse<AgendaEvent>) =>
        dispatch(getAgendaEvent(data))
      );
  };
};

export const getEventMedia = (eventMedia: EventMedia): Action<EventMedia> => {
  return {
    type: ACTION.GET_EVENT_MEDIA,
    payload: eventMedia,
  };
};

export const updateSelectedDate = (date: string): Action<string> => {
  return {
    type: ACTION.UPDATE_SELECTED_DATE,
    payload: date,
  };
};

export const updateNextLink = (isNextLink: boolean): Action<boolean> => {
  return {
    type: ACTION.UPDATE_NEXT_LINK,
    payload: isNextLink,
  };
};
export const updatePastLink = (isPastLink: boolean): Action<boolean> => {
  return {
    type: ACTION.UPDATE_PAST_LINK,
    payload: isPastLink,
  };
};

export const getEventMediaAsync = (id: number) => {
  return (dispatch: AppDispatch): Promise<Action<EventMedia>> => {
    return v1Api
      .get(`/EventsMedia/${id}`)
      .then(({ data }: AxiosResponse<EventMedia>) =>
        dispatch(
          getEventMedia({
            id: data?.id,
            videoUrl: data?.videoUrl,
            eventBookmarks: data?.eventBookmarks,
            externalVideoUrl: data?.externalVideoUrl,
            isLiveStreaming: data?.isLiveStreaming,
            eventCCKey: data?.eventCCKey,
            pauseMessage: data?.pauseMessage,
            streamingStatus: data?.streamingStatus,
            transcriptionUrl: data?.transcriptionUrl,
            closedCaptionUrl: data?.closedCaptionUrl,
            closedCaptionTracks: data?.closedCaptionTracks,
            defaultFileName: data?.defaultFileName,
            showTranscoding: data.showTranscoding,
            audioDescriptionUrl: data?.audioDescriptionUrl,
          })
        )
      );
  };
};

export const cleanEventMedia = (): Action<undefined> => {
  return {
    type: ACTION.CLEAN_EVENT_MEDIA,
  };
};

export const setJwplayerVideo = (
  jwplayerVideo: JwplayerVideo | null = null
): Action<JwplayerVideo | null> => {
  const tracks: JwplayTracks[] = [];
  if (
    jwplayerVideo?.bookmark?.length &&
    jwplayerVideo?.tracks?.filter(track => track.kind === "chapters").length ===
      0
  ) {
    let vttString = "WEBVTT\n\n";
    let i = 1;

    const makeBookmarks = (bookmark: Bookmark) => {
      vttString += `Chapter ${i}\n${bookmark.markerTimeHHMMSSFormat}.000  --> ${bookmark.markerTimeHHMMSSFormat}.000\n${bookmark.markerTitle}\n\n`;
      bookmark.additionalBookmarks?.forEach(makeBookmarks);
      i++;
    };

    if (jwplayerVideo.bookmark) {
      jwplayerVideo.bookmark = jwplayerVideo.bookmark.filter(
        (value, index, self) => {
          return (
            index ===
            self.findIndex(
              item =>
                item.markerTimeHHMMSSFormat === value.markerTimeHHMMSSFormat
            )
          );
        }
      );

      const sortedBookmarks = jwplayerVideo.bookmark
        .flat()
        .sort((a, b) => a.markerTimeStart - b.markerTimeStart);
      sortedBookmarks.forEach(makeBookmarks);
    }

    const chapterFile = URL.createObjectURL(
      new Blob([vttString], {
        type: "text/vtt",
      })
    );

    tracks.push({
      file: chapterFile,
      kind: "chapters",
    });
  }

  if (jwplayerVideo?.tracks?.length) {
    tracks.push(...jwplayerVideo.tracks);
  }

  return {
    type: ACTION.SET_JWPLAYER_VIDEO,
    payload: {
      ...jwplayerVideo,
      tracks,
    },
  };
};

export const cleanJwplayerVideo = (): Action<undefined> => {
  return {
    type: ACTION.CLEAN_JWPLAYER_VIDEO,
  };
};

export const getCategoriesAsync = () => {
  return (dispatch: AppDispatch): Promise<Action<EventCategory>> => {
    return v1Api
      .get("/EventCategories")
      .then(({ data }: AxiosResponse<ApiResponseValueKey<EventCategory>>) =>
        dispatch({
          type: ACTION.GET_CATEGORIES,
          payload: data.value,
        })
      );
  };
};

export const updateEventFilters = (
  filters: EventFilters | undefined = undefined
): Action<EventFilters> => {
  return {
    type: ACTION.UPDATE_EVENT_FILTERS,
    payload: filters,
  };
};

export const getSearchResultsAsync = (
  filters: EventFilters,
  skipAzureSearch: number
) => {
  return (dispatch: AppDispatch): Promise<Action<SearchResult | undefined>> => {
    dispatch(cleanEventList());
    const subFilter = filters.contentTypes
      ? filters.contentTypes.join(",")
      : "";

    const categoryFilter = filters.categories
      ? filters.categories.join(",")
      : "";

    let fromDate = "";
    let toDate = "";

    if (
      filters.search === "" &&
      filters.contentTypes &&
      filters.contentTypes?.length > 0
    ) {
      filters.search = "*";
    }

    if (filters.date?.from != null) {
      fromDate = toLocalISODateString(new Date(filters.date.from));
    }
    if (filters.date?.to != null) {
      toDate = toLocalISODateString(new Date(filters.date.to));
    }
    let url = `/Search?search=${filters.search}`;

    if (categoryFilter) {
      url += `&categoryIds=${categoryFilter}`;
    }
    if (fromDate) {
      url += `&startDate=${fromDate}`;
    }
    if (toDate) {
      url += `&endDate=${toDate}`;
    }
    if (subFilter) {
      url += `&subFilter=${subFilter}`;
    }
    if (skipAzureSearch) {
      url += `&skipAzureSearch=${skipAzureSearch}`;
    }
    if (filters.skip) {
      url += `&$skip=${filters.skip}`;
    }

    return v1Api.get(url).then(({ data }) =>
      dispatch({
        type:
          !filters.skip && !skipAzureSearch
            ? ACTION.GET_SEARCH_RESULTS
            : ACTION.GET_NEXT_SEARCH_RESULTS,
        payload: data?.value,
      })
    );
  };
};

export const cleanSearchResults = (): Action<undefined> => {
  return {
    type: ACTION.CLEAR_SEARCH_RESULTS,
  };
};

export const getPortalCustomization = (
  portalCustomization: PortalCustomization
): Action<PortalCustomization> => {
  return {
    type: ACTION.GET_PORTAL_CUSTOMIZATION,
    payload: portalCustomization,
  };
};

export const getPortalCustomizationAsync = () => {
  return (dispatch: AppDispatch): Promise<Action<PortalCustomization>> => {
    return v1Api
      .get("/Settings/GetPublicPortalCustomizations")
      .then(({ data }: AxiosResponse<PortalCustomization>) =>
        dispatch(getPortalCustomization(data))
      );
  };
};

export const setLoading = (isLoading: boolean): Action<boolean> => {
  return {
    type: ACTION.SET_LOADING,
    payload: isLoading,
  };
};

export const setScrollToToday = (scrollToToday: boolean): Action<boolean> => {
  return {
    type: ACTION.SET_SCROLL_TO_TODAY,
    payload: scrollToToday,
  };
};

export const submitPublicCommentAsync = (params: {
  eventId: number;
  name: string;
  mapsAddress?: string;
  address?: string;
  address2?: string;
  state: string;
  city: string;
  zipCode: string;
  organization?: string;
  phoneNumber: string;
  email: string;
  participationMethod: ParticipationMethodEnum;
  itemId: number;
  isInterpreterRequired?: boolean;
  interpreterLanguage?: string;
}) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    try {
      await v1Api.post(
        `/Events/PublicCommentSignUp`,
        {
          ...params,
          participationMethod: params.participationMethod.toString(),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      dispatch({ type: ACTION.SUBMIT_PUBLIC_COMMENT_SUCCESS });
    } catch (error: unknown) {
      let msg = "Failed to submit request. Please try again later.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const apiErr = error as ApiError;
        const data = apiErr.response?.data;
        if (data) {
          msg = data.message ?? data.Message ?? msg;
        }
      }

      dispatch({ type: ACTION.SUBMIT_PUBLIC_COMMENT_FAILURE });
      throw new Error(msg);
    }
  };
};

export const submitPublicCommentWrittenAsync = (params: {
  eventId: number;
  name: string;
  mapsAddress?: string;
  address1: string;
  address2: string;
  state: string;
  city: string;
  zipCode: string;
  organization?: string;
  phoneNumber: string;
  email: string;
  positionMethod: PositionMethodEnum;
  itemId: number;
  comment: string;
  file?: {
    fileName: string;
    fileContent: string;
    fileSize: number;
    contentType: string;
  };
}) => {
  return async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await v1Api.post(
        `/Events/PublicCommentWritten`,
        {
          ...params,
          positionMethod: params.positionMethod.toString(),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      dispatch({ type: ACTION.SUBMIT_WRITTEN_COMMENT_SUCCESS });
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<{ error?: string }>;
      const errorMessage =
        apiError.response?.data?.error ||
        apiError.message ||
        "Failed to submit comment";
      dispatch({ type: ACTION.SUBMIT_WRITTEN_COMMENT_FAILURE });
      return {
        success: false,
        error: errorMessage,
      };
    }
  };
};

export const submitEmailForValidationAsync = (params: {
  eventId: number;
  email: string;
  itemId: number;
}) => {
  return async (dispatch: AppDispatch): Promise<number> => {
    try {
      const response = await v1Api.post(`Events/RequestVerification`, params, {
        headers: { "Content-Type": "application/json" },
      });
      const { speakerRequestId } = response.data as {
        speakerRequestId: number;
        Message: string;
      };
      dispatch({
        type: ACTION.SUBMIT_EMAIL_VALIDATION_SUCCESS,
        payload: { speakerRequestId },
      });
      return speakerRequestId;
    } catch (error: unknown) {
      let msg = "Failed to send code. Please try again.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const apiErr = error as ApiError;
        const data = apiErr.response?.data;
        if (data) {
          msg = data.message ?? data.Message ?? msg;
        }
      }

      dispatch({ type: ACTION.SUBMIT_EMAIL_VALIDATION_FAILURE });
      throw new Error(msg);
    }
  };
};

export const submitValidationCodeAsync = (params: {
  verificationCode: string;
  email: string;
  speakerRequestId: number;
}) => {
  return async (dispatch: AppDispatch): Promise<void> => {
    try {
      await v1Api.post(
        `/Events/VerifyCode`,
        {
          id: params.speakerRequestId,
          email: params.email,
          code: params.verificationCode,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      dispatch({ type: ACTION.SUBMIT_CODE_VALIDATION_SUCCESS });
    } catch (error) {
      dispatch({ type: ACTION.SUBMIT_CODE_VALIDATION_FAILURE });
      throw error;
    }
  };
};

export const verifyCaptcha = (captchaValue: string | null) => {
  return async (
    dispatch: AppDispatch
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      await v1Api.post(
        `/Events/VerifyCaptcha`,
        {
          token: captchaValue,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      dispatch({ type: ACTION.VERIFY_RECAPTCHA_SUCCESS });
      return { success: true };
    } catch (error) {
      const apiError = error as AxiosError<{ error?: string }>;
      const errorMessage =
        apiError.response?.data?.error ||
        apiError.message ||
        "Failed to verify recaptcha";
      dispatch({ type: ACTION.VERIFY_RECAPTCHA_FAILURE });
      return {
        success: false,
        error: errorMessage,
      };
    }
  };
};
